/* eslint-disable max-lines */
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import StepShell from '../StepShell';
import { colors, layout } from '../styles';
import StateService from '../../../../../services/State';
import usePreEmploymentDraft from './preEmployment/usePreEmploymentDraft';
import useSaveLaterHandler from './preEmployment/useSaveLaterHandler';
import DateInput from '../components/DateInput';
import { formatPhone } from '../utils/numberValidation';
import { EmploymentHistoryEntry, emptyHistoryEntry } from './preEmployment/PreEmploymentTypes';
import { BaseStepProps } from './BaseStepProps';

const PreEmploymentHistoryStep = ({
  step,
  stepIndex,
  totalSteps,
  completedSteps,
  token,
  tokenData,
  applicationDraft,
  onBack,
  onPrevious,
  onNext,
  onSaveLater,
}: BaseStepProps) => {
  const { formData, updateForm, history, updateHistory, saveAsDraft, submitFinal, clear } = usePreEmploymentDraft(
    token,
    applicationDraft,
  );
  const { handleSaveLater } = useSaveLaterHandler({
    saveAsDraft: () => saveAsDraft({ token, userId: tokenData.userId, userName: tokenData.userName }),
    onSaveLater,
  });
  const [states, setStates] = useState<{ uf: string; name: string }[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    StateService('')
      .listSelect()
      .then((result: any) => setStates(result?.data || []))
      .catch(() => setStates([]));
  }, []);

  const updateEntry = (index: number, patch: Partial<EmploymentHistoryEntry>) => {
    const next = { ...history, history: [...history.history] };
    next.history[index] = { ...next.history[index], ...patch };
    updateHistory(next);
  };

  const addEntry = () => {
    updateHistory({ ...history, history: [...history.history, emptyHistoryEntry()] });
  };

  const removeEntry = (index: number) => {
    if (history.history.length <= 1) return;
    const next = { ...history, history: history.history.filter((_, i) => i !== index) };
    updateHistory(next);
  };

  const canSubmit =
    !!history.applicantName &&
    history.history.some((h) => !!h.placeOfEmployment) &&
    !!formData.signature.trim() &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await submitFinal({ token, userId: tokenData.userId, userName: tokenData.userName });
      clear();
      toast.success('Application submitted.');
      onNext();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Could not submit application.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StepShell
      stepNumber={stepIndex + 1}
      totalSteps={totalSteps}
      completedSteps={completedSteps}
      title={step.title}
      onBack={onBack}
      onPrevious={onPrevious}
      onSaveLater={handleSaveLater}
      onNext={handleSubmit}
      nextLabel={submitting ? 'Submitting...' : 'Submit Application'}
      nextDisabled={!canSubmit}
    >
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Employment History</h3>
      <p style={{ color: colors.muted, fontSize: 14, marginTop: 6 }}>
        List your most recent employers. <strong>Employment history must cover the last 5 years.</strong> The
        application is finalized when you submit this step.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={layout.label}>Applicant Name *</label>
          <input
            type="text"
            value={history.applicantName}
            onChange={(e) => updateHistory({ ...history, applicantName: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Position Applied For</label>
          <input
            type="text"
            value={history.positionAppliedFor}
            onChange={(e) => updateHistory({ ...history, positionAppliedFor: e.target.value })}
            style={layout.input}
          />
        </div>
      </div>

      {history.history.map((entry, index) => (
        <div
          key={index}
          style={{
            background: '#F9FAFB',
            border: `1px solid ${colors.border}`,
            borderRadius: 10,
            padding: 14,
            marginBottom: 14,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <strong style={{ fontSize: 14 }}>Employer #{index + 1}</strong>
            {history.history.length > 1 && (
              <button
                type="button"
                onClick={() => removeEntry(index)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: colors.danger,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Remove
              </button>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 8 }}>
            <div>
              <label style={layout.label}>Place of Employment</label>
              <input
                type="text"
                value={entry.placeOfEmployment}
                onChange={(e) => updateEntry(index, { placeOfEmployment: e.target.value })}
                style={layout.input}
              />
            </div>
            <div>
              <label style={layout.label}>Phone</label>
              <input
                type="tel"
                value={entry.phone}
                onChange={(e) => updateEntry(index, { phone: formatPhone(e.target.value) })}
                placeholder="(555) 123-4567"
                style={layout.input}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 8 }}>
            <div>
              <label style={layout.label}>Address</label>
              <input
                type="text"
                value={entry.address}
                onChange={(e) => updateEntry(index, { address: e.target.value })}
                style={layout.input}
              />
            </div>
            <div>
              <label style={layout.label}>City</label>
              <input
                type="text"
                value={entry.city}
                onChange={(e) => updateEntry(index, { city: e.target.value })}
                style={layout.input}
              />
            </div>
            <div>
              <label style={layout.label}>State</label>
              <select
                value={entry.state}
                onChange={(e) => updateEntry(index, { state: e.target.value })}
                style={layout.select}
              >
                <option value="">Select...</option>
                {states.map((s) => (
                  <option key={s.uf} value={s.uf}>
                    {s.uf}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 }}>
            <div>
              <label style={layout.label}>From</label>
              <DateInput
                value={entry.datesOfEmploymentFrom}
                onChange={(v) => updateEntry(index, { datesOfEmploymentFrom: v })}
              />
            </div>
            <div>
              <label style={layout.label}>Until</label>
              <DateInput
                value={entry.datesOfEmploymentUntil}
                onChange={(v) => updateEntry(index, { datesOfEmploymentUntil: v })}
                disabled={entry.currentlyEmployed}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={entry.currentlyEmployed}
                  onChange={(e) => updateEntry(index, { currentlyEmployed: e.target.checked })}
                />
                Currently employed here
              </label>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 }}>
            <div>
              <label style={layout.label}>Position Held</label>
              <input
                type="text"
                value={entry.positionHeld}
                onChange={(e) => updateEntry(index, { positionHeld: e.target.value })}
                style={layout.input}
              />
            </div>
            <div>
              <label style={layout.label}>Supervisor</label>
              <input
                type="text"
                value={entry.supervisor}
                onChange={(e) => updateEntry(index, { supervisor: e.target.value })}
                style={layout.input}
              />
            </div>
          </div>

          <div style={{ marginBottom: 8 }}>
            <label style={layout.label}>Reason for Leaving</label>
            <input
              type="text"
              value={entry.reasonForLeaving}
              onChange={(e) => updateEntry(index, { reasonForLeaving: e.target.value })}
              style={layout.input}
            />
          </div>

          <div>
            <label style={layout.label}>Job Description</label>
            <textarea
              rows={2}
              value={entry.jobDescription}
              onChange={(e) => updateEntry(index, { jobDescription: e.target.value })}
              style={{ ...layout.input, resize: 'vertical' }}
            />
          </div>
        </div>
      ))}

      <button type="button" onClick={addEntry} style={{ ...layout.secondaryBtn, marginBottom: 12 }}>
        + Add another employer
      </button>

      <div style={{ marginTop: 8 }}>
        <label style={layout.label}>Applicant&apos;s Signature (type your full legal name) *</label>
        <input
          type="text"
          value={formData.signature}
          onChange={(e) => updateForm({ signature: e.target.value })}
          style={layout.input}
        />
      </div>
    </StepShell>
  );
};

export default PreEmploymentHistoryStep;
